import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import Dropdown from "../components/Dropdown.jsx";
import Pagination from "../components/Pagination.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import "../styles/ParaList.css";


const FUNDS = ["Revenue", "Water Supply", "Elementry"];

function formatINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function ClosedParaList() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [paras, setParas] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [search, setSearch] = useState("");
  const [fund, setFund] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [dept, setDept] = useState("");
  const [zones, setZones] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    api
      .get("/zones/")
      .then((r) => setZones(r.data.results || r.data))
      .catch(() => {});
    api
      .get("/departments/")
      .then((r) => setDepartments(r.data.results || r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, status: "CLOSED" };
    if (search) params.search = search;
    if (fund) params.fund = fund;
    if (zoneFilter) params.zone = zoneFilter;
    if (dept) params.department = dept;
    api
      .get("/paras/", { params })
      .then((r) => {
        const results = r.data.results || r.data;
        setParas(results);
        setCount(r.data.count ?? results.length);
        const sum = results.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
        setTotalAmount(sum);
      })
      .finally(() => setLoading(false));
  }, [search, fund, zoneFilter, dept, page]);

  useEffect(() => {
    const id = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(id);
  }, [load, search]);

  const clearFilters = () => {
    setSearch("");
    setFund("");
    setZoneFilter("");
    setDept("");
    setPage(1);
  };

  const exportToCSV = () => {
    if (paras.length === 0) {
      window.alert("No closed paras to export.");
      return;
    }
    const headers = [
      "S.No", "Year", "Para No", "Department", "Description", "Amount",
      "Assigned To", "Closed Date",
    ];
    const rows = paras.map((p, i) => [
      p.s_no || i + 1,
      p.audit_year,
      p.para_no,
      p.department || "",
      ((lang === "ta" && p.description_ta ? p.description_ta : p.description) || "")
        .replace(/"/g, '""')
        .replace(/\n/g, " "),
      p.amount || "",
      p.assign_type === "COMBINED"
        ? (p.zone_names || []).join("; ")
        : p.assign_type === "MULTIPLE"
        ? (p.officer_names || []).join("; ")
        : p.assigned_officer_name || "",
      p.closed_at ? fmtDate(p.closed_at) : "",
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `closed-paras-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);
  const hasFilters = search || fund || zoneFilter || dept;

  return (
    <div className="page-paras">
      {/* ── Hero Banner ── */}
      <div
        className="closed-hero"
        style={{
          background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 60%, #2563eb 100%)",
          borderRadius: "12px",
          padding: "22px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
          boxShadow: "0 4px 20px rgba(30,58,138,0.18)",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: "26px" }}>🔒</span>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "#fff", letterSpacing: "-0.3px" }}>
              Closed Para List
            </div>
          </div>
          <div style={{ fontSize: "12.5px", color: "rgba(255,255,255,0.78)", marginTop: 2 }}>
            All paras that have been accepted &amp; permanently closed. Total:{" "}
            <strong style={{ color: "#fff" }}>{count}</strong> records
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.65)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
            Total Amount Recovered
          </div>
          <div style={{ fontSize: "24px", fontWeight: 800, color: "#86efac", marginTop: 3 }}>
            {formatINR(totalAmount)}
          </div>
        </div>
      </div>

      {/* ── Summary Stats ── */}
      <div
        className="closed-stats-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "10px",
          marginBottom: "14px",
        }}
      >
        {[
          { label: "Total Closed", value: count, icon: "🔒", color: "#1e3a8a", bg: "#eff6ff" },
          { label: "This Page", value: paras.length, icon: "📄", color: "#0f766e", bg: "#f0fdfa" },
          { label: "Total Amount", value: formatINR(totalAmount), icon: "💰", color: "#15803d", bg: "#f0fdf4" },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: stat.bg,
              border: `1.5px solid ${stat.color}22`,
              borderTop: `3px solid ${stat.color}`,
              borderRadius: "10px",
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ fontSize: "22px" }}>{stat.icon}</span>
            <div>
              <div style={{ fontSize: "18px", fontWeight: 800, color: stat.color, lineHeight: 1 }}>
                {stat.value}
              </div>
              <div style={{ fontSize: "10px", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 3 }}>
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters Toolbar ── */}
      <div className="toolbar">
        <div className="search-wrap">
          <i className="ti ti-search" />
          <input
            placeholder="Search closed paras..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Dropdown
          value={fund}
          onChange={(v) => {
            setFund(v);
            setPage(1);
          }}
          placeholder={t("filterFund")}
          options={[
            { value: "", label: t("filterFund") },
            ...FUNDS.map((f) => ({ value: f, label: f })),
          ]}
        />
        {["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user?.role) && (
          <Dropdown
            value={zoneFilter}
            onChange={(v) => {
              setZoneFilter(v);
              setPage(1);
            }}
            placeholder={t("filterZone")}
            options={[
              { value: "", label: t("filterZone") },
              ...zones.map((z) => ({ value: String(z.id), label: z.name })),
              { value: "main", label: t("mainOffice") },
            ]}
          />
        )}
        <Dropdown
          value={dept}
          onChange={(v) => {
            setDept(v);
            setPage(1);
          }}
          placeholder={t("filterDept")}
          options={[
            { value: "", label: t("filterDept") },
            ...departments.map((d) => ({ value: d.name, label: d.name })),
          ]}
        />
      </div>

      {/* ── Action Row ── */}
      <div className="action-row">
        {hasFilters && (
          <button className="btn-outline" onClick={clearFilters}>
            <i className="ti ti-circle-x" style={{ fontSize: "13px" }} /> Clear Filters
          </button>
        )}
        <button className="btn-outline" onClick={exportToCSV} title="Export to CSV">
          <i className="ti ti-download" style={{ fontSize: "13px" }} /> Export CSV
        </button>
        <button className="btn-outline" onClick={() => window.print()} title="Print">
          <i className="ti ti-printer" style={{ fontSize: "13px" }} /> Print
        </button>
      </div>

      {/* ── Table ── */}
      <div className="card" style={{ padding: 0 }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th className="col-sno">{t("sNo")}</th>
                <th className="col-year">{t("year")}</th>
                <th className="col-parano">
                  {lang === "en" ? (
                    <div className="header-bilingual">
                      <span className="eng-text">Para No</span>
                      <span className="tamil-text">(பத்தி எண்)</span>
                    </div>
                  ) : t("paraNo")}
                </th>
                <th className="col-dept">{t("department")}</th>
                <th className="col-desc">
                  {lang === "en" ? (
                    <div className="header-bilingual">
                      <span className="eng-text">Description</span>
                      <span className="tamil-text">(தணிக்க தகட சுருக்ம்)</span>
                    </div>
                  ) : t("description")}
                </th>
                <th className="col-amount">{t("amount")}</th>
                <th className="col-assignedto">{t("assignedTo")}</th>
                <th style={{ whiteSpace: "nowrap" }}>Closed Date</th>
                <th className="col-status">{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="muted" style={{ padding: 30, textAlign: "center" }}>
                    <span style={{ fontSize: 20, display: "block", marginBottom: 6 }}>⏳</span>
                    {t("loading")}
                  </td>
                </tr>
              )}
              {!loading && paras.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: "center" }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>🎉</div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "14px" }}>
                      No Closed Paras Found
                    </div>
                    <div style={{ color: "#64748b", fontSize: "12px", marginTop: 4 }}>
                      {hasFilters ? "Try adjusting your filters." : "Closed paras will appear here once a para is accepted & closed."}
                    </div>
                  </td>
                </tr>
              )}
              {paras.map((p, i) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/paras/${p.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="col-sno">{p.s_no || (page - 1) * PAGE_SIZE + i + 1}</td>
                  <td className="col-year">{p.audit_year}</td>
                  <td className="col-parano">
                    <span className="para-no">{p.para_no}</span>
                  </td>
                  <td className="col-dept">{p.department?.trim() ? p.department : "-"}</td>
                  <td
                    className="col-desc td-desc"
                    title={lang === "ta" && p.description_ta ? p.description_ta : p.description}
                  >
                    {lang === "ta" && p.description_ta ? p.description_ta : p.description}
                  </td>
                  <td className="col-amount r" style={{ fontWeight: 700, color: "#0f172a" }}>
                    {formatINR(p.amount)}
                  </td>
                  <td className="col-assignedto">
                    {p.assign_type === "COMBINED" ? (
                      p.zone_names?.join(", ") || <span className="muted">{t("none")}</span>
                    ) : p.assign_type === "MULTIPLE" ? (
                      p.officer_names?.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {p.officer_names.map((name, idx) => (
                            <div key={idx}>
                              <div>{name}</div>
                              {p.officer_zone_names?.[idx] && (
                                <div className="muted" style={{ fontSize: "11px" }}>
                                  ({p.officer_zone_names[idx]})
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="muted">{t("none")}</span>
                      )
                    ) : p.assign_type === "INDIVIDUAL" ? (
                      p.assigned_officer_name ? (
                        <div>
                          <div>{p.assigned_officer_name}</div>
                          {p.assigned_officer_zone_name && (
                            <div className="muted" style={{ fontSize: "11px" }}>
                              ({p.assigned_officer_zone_name})
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="muted">{t("none")}</span>
                      )
                    ) : (
                      <span className="muted">{t("none")}</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap", color: "#15803d", fontWeight: 600, fontSize: "11.5px" }}>
                    {p.closed_at ? fmtDate(p.closed_at) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="col-status">
                    <div className="status-cell-container">
                      <StatusBadge status={p.status} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pagination ── */}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
